// Google AI Studio (Gemini) Service per raccomandazioni artisti
import geminiLogger from './geminiLogger';
import SpotifyService from './spotify';
import { callGeminiViaProxy } from './geminiProxy';
import { 
  getBridgeConfig, 
  getFreeTextSearchConfig, 
  getSimilarArtistsConfig,
  getModelsForContext,
  GEMINI_MODELS_CONFIG 
} from '../config/aiProvider';

const USE_PROXY = process.env.REACT_APP_USE_GEMINI_PROXY === 'true';
const USE_GEMINI = process.env.REACT_APP_USE_GEMINI === 'true' || USE_PROXY;

// 🔑 POOL DI CHIAVI API - Rotazione automatica con fallback
// Le chiavi vengono caricate dalle variabili d'ambiente per sicurezza
const RAW_API_KEYS = [
  {
    key: process.env.REACT_APP_GEMINI_API_KEY,
    label: 'Key 1 (Primary)',
    usageCount: 0,
    lastUsed: null,
    bridgeOnly: false
  },
  {
    key: process.env.REACT_APP_GEMINI_API_KEY_2,
    label: 'Key 2',
    usageCount: 0,
    lastUsed: null,
    bridgeOnly: false
  },
  {
    key: process.env.REACT_APP_GEMINI_API_KEY_3,
    label: 'Key 3',
    usageCount: 0,
    lastUsed: null,
    bridgeOnly: false
  },
  {
    key: process.env.REACT_APP_GEMINI_API_KEY_4,
    label: 'Key 4 (Bridge)',
    usageCount: 0,
    lastUsed: null,
    bridgeOnly: true
  },
  {
    key: process.env.REACT_APP_GEMINI_API_KEY_5,
    label: 'Key 5 (Bridge)',
    usageCount: 0,
    lastUsed: null,
    bridgeOnly: true
  },
  {
    key: process.env.REACT_APP_GEMINI_API_KEY_6,
    label: 'Key 6 (Pay)',
    usageCount: 0,
    lastUsed: null,
    bridgeOnly: false
  }
].filter(k => k.key); // Rimuovi chiavi non configurate

const API_KEYS = RAW_API_KEYS.filter(k => !k.bridgeOnly);
const BRIDGE_API_KEYS = RAW_API_KEYS.filter(k => k.bridgeOnly);

let currentKeyIndex = 0; // Indice chiave corrente
let currentBridgeKeyIndex = 0; // Indice chiave bridge corrente
let currentBridgeFallbackIndex = API_KEYS.length > 1 ? 1 : 0; // Indice separato quando mancano chiavi bridge dedicate

// CONFIG: Scegli quale versione del prompt usare (1, 2, o 3)
const PROMPT_VERSION = 2; // Cambia qui per testare diverse versioni

// CONFIG: Abilita/disabilita salvataggio su database (per testing)
const ENABLE_DB_CACHE = false; // true = salva su Supabase

// CONFIG: Strategia a 2 fasi (2.5 Flash analizza → 2.0 Flash genera lista)
const USE_TWO_PHASE_STRATEGY = true; // true = analisi + generazione separate

// CONFIG: Genera e salva testo descrittivo artista (solo con strategia a 2 fasi)
const GENERATE_ARTIST_INFO = true; // true = genera testo discorsivo da salvare in DB

// FREE_TEXT_PROMPT_STYLE rimosso - usiamo solo una versione semplice

// Modelli Gemini ora vengono da aiProvider.js (configurazione unificata)
// Usa GEMINI_MODELS_CONFIG per riferimento, getModelsForContext() per ottenere modelli per contesto
const GEMINI_MODELS = GEMINI_MODELS_CONFIG; // Mantenuto per retrocompatibilità, ma usa getModelsForContext()

/**
 * 🔑 Gestione intelligente delle chiavi API
 */
const getCurrentApiKey = () => {
  if (API_KEYS.length === 0) return null;
  const apiKeyObj = API_KEYS[currentKeyIndex];
  apiKeyObj.usageCount++;
  apiKeyObj.lastUsed = new Date().toISOString();
  return apiKeyObj;
};

const getCurrentBridgeApiKey = () => {
  if (BRIDGE_API_KEYS.length > 0) {
    const index = currentBridgeKeyIndex % BRIDGE_API_KEYS.length;
    const apiKeyObj = BRIDGE_API_KEYS[index];
    apiKeyObj.usageCount++;
    apiKeyObj.lastUsed = new Date().toISOString();
    return apiKeyObj;
  }

  if (API_KEYS.length === 0) {
    return null;
  }

  if (API_KEYS.length === 1) {
    const apiKeyObj = API_KEYS[0];
    apiKeyObj.usageCount++;
    apiKeyObj.lastUsed = new Date().toISOString();
    return apiKeyObj;
  }

  let index = currentBridgeFallbackIndex % API_KEYS.length;
  if (API_KEYS.length > 1 && index === currentKeyIndex) {
    index = (index + 1) % API_KEYS.length;
  }

  const apiKeyObj = API_KEYS[index];
  apiKeyObj.usageCount++;
  apiKeyObj.lastUsed = new Date().toISOString();

  currentBridgeFallbackIndex = index;

  return apiKeyObj;
};

const rotateToNextKey = () => {
  if (API_KEYS.length <= 1) return false; // Nessuna rotazione possibile
  currentKeyIndex = (currentKeyIndex + 1) % API_KEYS.length;
  console.log(`%c🔄 [API Keys] Rotazione → ${API_KEYS[currentKeyIndex].label}`, 'background: #FF9800; color: white; padding: 4px 8px; border-radius: 3px; font-weight: bold;');
  return true;
};

const rotateToNextBridgeKey = () => {
  if (BRIDGE_API_KEYS.length > 0) {
    if (BRIDGE_API_KEYS.length <= 1) return false;
    currentBridgeKeyIndex = (currentBridgeKeyIndex + 1) % BRIDGE_API_KEYS.length;
    console.log(`%c🔄 [API Keys] Rotazione Bridge → ${BRIDGE_API_KEYS[currentBridgeKeyIndex].label}`, 'background: #00BCD4; color: white; padding: 4px 8px; border-radius: 3px; font-weight: bold;');
    return true;
  }
  if (API_KEYS.length <= 1) return false;

  currentBridgeFallbackIndex = (currentBridgeFallbackIndex + 1) % API_KEYS.length;
  if (currentBridgeFallbackIndex === currentKeyIndex) {
    currentBridgeFallbackIndex = (currentBridgeFallbackIndex + 1) % API_KEYS.length;
  }
  console.log(`%c🔄 [API Keys] Rotazione Bridge (main pool) → ${API_KEYS[currentBridgeFallbackIndex].label}`, 'background: #00BCD4; color: white; padding: 4px 8px; border-radius: 3px; font-weight: bold;');
  return true;
};

const getApiKeyStats = () => {
  const mainCurrent = API_KEYS.length > 0 ? API_KEYS[currentKeyIndex % API_KEYS.length] : null;
  const bridgeCurrent = BRIDGE_API_KEYS.length > 0 ? BRIDGE_API_KEYS[currentBridgeKeyIndex % BRIDGE_API_KEYS.length] : null;
  return RAW_API_KEYS.map((k) => ({
    label: k.label,
    isCurrent: (!k.bridgeOnly && mainCurrent === k) || (k.bridgeOnly && bridgeCurrent === k),
    usageCount: k.usageCount,
    lastUsed: k.lastUsed,
    bridgeOnly: !!k.bridgeOnly
  }));
};

// Cache per evitare chiamate duplicate
const geminiCache = new Map();

const attemptProxyCall = async ({
  model,
  version = 'v1beta',
  prompt,
  systemInstruction = null, // Nuovo: istruzioni statiche per cache optimization
  generationConfig,
  context,
  keyLabel = 'proxy',
  provider = 'gemini'
}) => {
  if (!USE_PROXY) {
    return { data: null, viaProxy: false };
  }

  const proxyResult = await callGeminiViaProxy({
    model,
    version,
    prompt,
    systemInstruction,
    generationConfig,
    context,
    keyLabel,
    provider
  });

  if (proxyResult && !proxyResult.error) {
    return { data: proxyResult, viaProxy: true };
  }

  return { data: null, viaProxy: true };
};

const callGeminiDirectWithKeys = async ({
  model,
  prompt,
  generationConfig,
  context,
  userRequest,
  language,
  startTime,
  promptLength
}) => {
  if (API_KEYS.length === 0) {
    return null;
  }

  for (let keyAttempt = 0; keyAttempt < API_KEYS.length; keyAttempt++) {
    const apiKeyObj = getCurrentApiKey();
    let timeoutId = null;
    let controller = null;

    geminiLogger.logApiCall({
      context,
      action: 'start',
      model: `${model.label} (${apiKeyObj.label})`,
      modelAttempt: 1,
      keyAttempt: keyAttempt + 1,
      promptLength,
      language,
      userRequest: userRequest.substring(0, 120)
    });

    try {
      controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), 120000);

      const response = await fetch(
        `https://generativelanguage.googleapis.com/${model.version || 'v1beta'}/models/${model.name}:generateContent?key=${apiKeyObj.key}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig
          })
        }
      );

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || `HTTP ${response.status}`;

        geminiLogger.logApiError({
          context,
          artistName: userRequest.substring(0, 50),
          model: `${model.label} (${apiKeyObj.label})`,
          responseTime,
          status: response.status,
          errorMessage,
          errorDetails: errorData.error,
          promptLength,
          keyAttempt: keyAttempt + 1
        });

        if (response.status === 429 || response.status === 503) {
          rotateToNextKey();
          continue;
        }

        continue;
      }

      const data = await response.json();
      const result = processFreeTextProxyResponse({
        data,
        model,
        userRequest,
        prompt,
        language,
        startTime,
        promptLength
      });

      if (result) {
        return result;
      }
    } catch (error) {
      const responseTime = Date.now() - startTime;
      geminiLogger.logApiError({
        context,
        artistName: userRequest.substring(0, 50),
        model: `${model.label} (${apiKeyObj.label})`,
        responseTime,
        errorMessage: error.message,
        errorStack: error.stack,
        promptLength,
        keyAttempt: keyAttempt + 1
      });

      if (error.name === 'AbortError' && keyAttempt < API_KEYS.length - 1) {
        rotateToNextKey();
        continue;
      }
    }
  }

  console.warn(`[${model.label}] Tutte le chiavi locali hanno fallito`);
  return null;
};

const parseGeminiJsonString = (rawText) => {
  let cleanedText = rawText.trim();
  const jsonMatch = cleanedText.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    cleanedText = jsonMatch[0];
  }
  return JSON.parse(cleanedText);
};

const processFreeTextProxyResponse = ({
  data,
  model,
  userRequest,
  prompt,
  language,
  startTime,
  promptLength
}) => {
  const responseTime = Date.now() - startTime;
  const finishReason = data.candidates?.[0]?.finishReason;
  const usageMetadata = data.usageMetadata;
  const text = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

  if (!text) {
    geminiLogger.logApiError({
      context: 'free_text_search',
      artistName: userRequest.substring(0, 50),
      model: `${model.label} (Proxy)`,
      responseTime,
      errorMessage: 'Empty response'
    });
    return null;
  }

  let parsedData;
  try {
    parsedData = parseGeminiJsonString(text);
  } catch (error) {
    geminiLogger.logApiError({
      context: 'free_text_search',
      artistName: userRequest.substring(0, 50),
      model: `${model.label} (Proxy)`,
      responseTime,
      errorMessage: `JSON parse error: ${error.message}`,
      textSample: text.substring(0, 500)
    });
    return null;
  }

  if (parsedData.valid === false) {
    return {
      valid: false,
      message: parsedData.message || 'Non ho abbastanza informazioni per consigliarti musica.'
    };
  }

  if (!parsedData.valid || !Array.isArray(parsedData.artists)) {
    geminiLogger.logApiError({
      context: 'free_text_search',
      artistName: userRequest.substring(0, 50),
      model: `${model.label} (Proxy)`,
      responseTime,
      errorMessage: 'Invalid response format: missing or invalid artists array'
    });
    return null;
  }

  const items = parsedData.artists.map(item => ({
    name: item.name,
    match: (item.match || 0.8).toString(),
    genre: item.genre || '',
    origin: item.origin || '',
    url: `https://www.last.fm/music/${encodeURIComponent(item.name)}`
  }));

  geminiLogger.logApiSuccess({
    context: 'free_text_search',
    artistName: userRequest.substring(0, 50),
    model: `${model.label} (Proxy)`,
    responseTime,
    promptLength,
    outputLength: text.length,
    usageMetadata
  });

  return {
    valid: true,
    title: parsedData.title || (language === 'it' ? parsedData.title_it : parsedData.title_en) || '',
    analysis: parsedData.analysis || (language === 'it' ? parsedData.analysis_it : parsedData.analysis_en) || '',
    items,
    language
  };
};

/**
 * PROMPT FREE TEXT SEARCH - Ottimizzato per cache
 * Separa istruzioni statiche (cachate) da contenuto dinamico (non cachato)
 * I prompt restano IDENTICI, solo separati per ottimizzazione cache
 */
const getFreeTextSearchPrompt = (userRequest, searchType = 'artist', language = 'en') => {
  const targetLanguage = language === 'it' ? 'Italian' : 'English';
  
  // Solo anno (non cambia ogni minuto, mantiene cache valida più a lungo)
  const currentYear = new Date().getFullYear();

  // PARTE STATICA (CACHATA) - system_instruction
  // Contiene tutte le regole fisse che non cambiano tra richieste
  const systemInstruction = `Current date and time: ${currentYear} (Rome timezone)

IMPORTANT: 
- Provide the maximum number of coherent artists possible, ensuring a minimum of 20 and a maximum of 30 results
- Always aim for more results rather than fewer

Return ONLY this JSON (no thinking, no explanation):
{
  "valid": true,
  "title": "Title in ${targetLanguage} (max 10 words)",
  "analysis": "Brief analysis in ${targetLanguage} (max 120 words)",
  "artists": [
    {"name": "EXACT name", "genre": "Spotify genres (comma-separated, e.g. 'pop, adult contemporary, italian pop')", "origin": "Country"}
  ]
}
RULES:
- **CRITICAL: ONLY REAL ARTISTS THAT ACTUALLY EXIST - DO NOT INVENT ARTISTS - DO NOT CREATE FAKE ARTIST NAMES - VERIFY EACH ARTIST EXISTS BEFORE INCLUDING THEM**
- Max 30 artists
- **CRITICAL: Preserve EXACT spelling, including special characters, accents, and diacritics (e.g., "La Niña" NOT "La Nina", "José" NOT "Jose", "Björk" NOT "Bjork")**
- Use the official/correct spelling of artist names as they appear in music databases`;

  // PARTE DINAMICA (NON CACHATA) - user message
  // Solo la richiesta utente che cambia ogni volta
  const userMessage = `User wants: "${userRequest}"`;

  return {
    systemInstruction,
    userMessage
  };
};

/**
 * PROMPT FREE TEXT BRIDGE - Ottimizzato per cache
 * Separa istruzioni statiche (cachate) da contenuto dinamico (non cachato)
 * I prompt restano IDENTICI, solo separati per ottimizzazione cache
 */
const getFreeTextBridgePrompt = (userRequest, language = 'en') => {
  const isItalian = language === 'it';
  const targetLanguageLabel = isItalian ? 'Italian' : 'English';
  
  // PARTE STATICA (CACHATA) - system_instruction
  // Voce: Mr. Foundly in prima persona singolare (è lui che cerca)
  const systemInstruction = `You are Mr. Foundly's bridge message generator. The message is spoken by Mr. Foundly in first person (I, not "we" or "Foundly").

VALIDATION: If the prompt is NOT about music (e.g. "hi", "test", too short), respond ONLY with this JSON:
${isItalian 
  ? `{"valid": false, "message": "Ops! Mi dispiace, ma questa richiesta non riguarda la musica. Prova a chiedermi qualcosa su artisti, album, canzoni o generi musicali!"}`
  : `{"valid": false, "message": "Oops! Sorry, but this request isn't about music. Try asking me something about artists, albums, songs, or music genres!"}`}

If valid, write a 60-80 word message in first person as Mr. Foundly. Start with "Ciao, sono Mr. Foundly" (Italian) or "Hi, I'm Mr. Foundly" (English), then describe what you are looking for or analyzing for the user. Warm tone. I recommend artists (not songs/albums). Use paragraphs. Conclude saying the answer is coming. NO JSON, NO lists, NO emojis. Complete the FULL message.

Reply in ${targetLanguageLabel}.`;

  // PARTE DINAMICA (NON CACHATA) - user message
  // Solo la richiesta utente che cambia ogni volta
  const userMessage = `User prompt: "${userRequest.trim()}"`;

  return {
    systemInstruction,
    userMessage
  };
};

// Esponi cache e funzioni API keys globalmente per debugging
if (typeof window !== 'undefined') {
  window.geminiCache = geminiCache;
  window.geminiApiKeys = {
    stats: getApiKeyStats,
    rotate: rotateToNextKey,
    getCurrent: () => (API_KEYS.length > 0 ? API_KEYS[currentKeyIndex]?.label : null),
    bridgeStats: () => BRIDGE_API_KEYS.map((k, i) => ({
      label: k.label,
      isCurrent: i === (currentBridgeKeyIndex % Math.max(BRIDGE_API_KEYS.length, 1)),
      usageCount: k.usageCount,
      lastUsed: k.lastUsed
    })),
    rotateBridge: rotateToNextBridgeKey,
    getCurrentBridge: () => {
      if (BRIDGE_API_KEYS.length > 0) {
        return BRIDGE_API_KEYS[currentBridgeKeyIndex]?.label;
      }
      return API_KEYS.length > 0 ? API_KEYS[currentKeyIndex]?.label : null;
    }
  };
  
  // Funzione per pulire la cache dalla console
  window.clearGeminiCache = () => {
    const count = geminiCache.size;
    geminiCache.clear();
    console.log(`%c[Gemini] Cache cleared - ${count} entries removed`, 'background: #FF9800; color: white; padding: 4px 8px; border-radius: 3px; font-weight: bold;');
  };
  
  // Log stato Gemini all'avvio
  if (USE_GEMINI && RAW_API_KEYS.length > 0) {
    console.log('%c[Gemini] CONFIGURAZIONE ATTIVA', 'background: #4CAF50; color: white; padding: 8px 16px; border-radius: 4px; font-weight: bold; font-size: 14px;');
    
    // Mostra pool di chiavi API
    console.log(`%c🔑 API Keys Pool: ${API_KEYS.length} chiavi principali disponibili`, 'background: #2196F3; color: white; padding: 4px 8px; border-radius: 3px; font-weight: bold;');
    if (API_KEYS.length === 0) {
      console.log('   ⚠️ Nessuna chiave principale configurata (verranno usate solo le chiavi bridge, se presenti)');
    } else {
      API_KEYS.forEach((k, i) => {
        const indicator = i === currentKeyIndex ? '🟢' : '⚪';
        console.log(`   ${indicator} ${k.label}`);
      });
      console.log('   → Rotazione automatica in caso di rate limit o errori 503');
    }
    if (BRIDGE_API_KEYS.length > 0) {
      console.log(`%c🧩 Bridge Keys: ${BRIDGE_API_KEYS.length} dedicate al messaggio 2.0 Flash`, 'background: #00BCD4; color: white; padding: 4px 8px; border-radius: 3px; font-weight: bold;');
      BRIDGE_API_KEYS.forEach((k, i) => {
        const indicator = i === currentBridgeKeyIndex ? '🟢' : '⚪';
        console.log(`   ${indicator} ${k.label}`);
      });
      console.log('   → Usate esclusivamente per il bridge (rotazione separata)');
    }
    console.log('');
    
    if (USE_TWO_PHASE_STRATEGY && PROMPT_VERSION === 2) {
      console.log('%c🚀 STRATEGIA A 2 FASI ATTIVA', 'background: #9C27B0; color: white; padding: 4px 8px; border-radius: 3px; font-weight: bold;');
      console.log('   Fase 1: Gemini 2.5 Flash → (fallback) → 2.0 Flash (Analisi)');
      console.log('   Fase 2: Gemini 2.0 Flash (Lista + Info)');
      if (GENERATE_ARTIST_INFO) {
        console.log('   ✍️  Generazione testo descrittivo → Salvataggio in DB');
      }
      console.log('');
    }
    
    console.log('%c🤖 Modelli disponibili:', 'color: #2196F3; font-weight: bold;');
    GEMINI_MODELS_CONFIG.forEach((m, i) => {
      console.log(`   ${i + 1}. ${m.label} (${m.limits}) - ${m.description}`);
    });
    console.log(`%c📝 Prompt Version: ${PROMPT_VERSION}`, 'color: #9C27B0; font-weight: bold;');
    console.log(`%c💾 DB Cache: ${ENABLE_DB_CACHE ? 'Enabled' : 'Disabled'}`, 'color: #FF9800; font-weight: bold;');
    console.log('%c💡 Comandi:', 'color: #00BCD4; font-weight: bold;');
    console.log('   window.clearGeminiCache() - Pulisci cache per forzare nuove chiamate');
    console.log('   window.geminiLogger.printReport() - Mostra report log');
    console.log('   window.geminiApiKeys.stats() - Statistiche utilizzo chiavi API');
    console.log('   window.geminiApiKeys.getCurrent() - Chiave API attualmente in uso');
    console.log('');
  } else {
    console.log('[LastFM] Using Last.fm only (Gemini disabled)');
  }
}

// PROMPT V1/V2/V3 RIMOSSI - non più utilizzati
// getTwoPhaseRecommendations RIMOSSA - non più utilizzata
// CODICE RESIDUO COMMENTATO PER EVITARE ERRORI DI SINTASSI
/*
  const phase1StartTime = Date.now();
  
  // Per artisti simili, usa solo 2.0 Flash (più veloce per i test)
  // Per daily discovery, lascia tutti i modelli disponibili
  const PHASE1_MODELS = [
    { name: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash', maxTokens: 8192 }
  ];
  
  let phase1Response = null;
  let phase1Data = null;
  let phase1Time = 0;
  let usedModelLabel = null;
  
  // Loop sui modelli (solo 2.0 Flash per artisti simili)
  for (let modelIdx = 0; modelIdx < PHASE1_MODELS.length; modelIdx++) {
    const analysisModel = PHASE1_MODELS[modelIdx];
    const apiVersion = 'v1beta';
    
    
    geminiLogger.logApiCall({
      context: 'two_phase_analysis',
      phase: 1,
      artistName,
      spotifyId,
      genres,
      model: analysisModel.label,
      promptLength: phase1Prompt.length
    });
    
    // Loop sulle chiavi API per questo modello
    for (let keyAttempt = 0; keyAttempt < API_KEYS.length; keyAttempt++) {
      const apiKeyObj = getCurrentApiKey();
      
      try {
        const attemptStart = Date.now();
        
        const response = await fetch(
          `https://generativelanguage.googleapis.com/${apiVersion}/models/${analysisModel.name}:generateContent?key=${apiKeyObj.key}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: phase1Prompt }] }],
              generationConfig: {
                temperature: 0.7,
                topK: 40,
                topP: 0.95,
                maxOutputTokens: analysisModel.maxTokens
              }
            })
          }
        );
        
        phase1Time = Date.now() - attemptStart;
        
        if (response.ok) {
          phase1Response = response;
          phase1Data = await response.json();
          usedModelLabel = analysisModel.label;
          break; // Successo! Esci dal loop delle chiavi
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMsg = errorData.error?.message || 'Unknown error';
          
          console.warn(`⚠️ [Phase 1] ${analysisModel.label} con ${apiKeyObj.label} errore (${response.status}): ${errorMsg}`);
          
          geminiLogger.logApiError({
            context: 'two_phase_analysis',
            phase: 1,
            artistName,
            model: `${analysisModel.label} (${apiKeyObj.label})`,
            status: response.status,
            errorMessage: errorMsg,
            responseTime: phase1Time
          });
          
          // Se è rate limit (429) o overloaded (503), prova la prossima chiave
          if ((response.status === 429 || response.status === 503) && keyAttempt < API_KEYS.length - 1) {
            rotateToNextKey();
            continue; // Prova prossima chiave
          } else if (keyAttempt >= API_KEYS.length - 1) {
            console.warn(`❌ [Phase 1] ${analysisModel.label}: tutte le chiavi fallite`);
            break; // Esci dal loop chiavi, passa al prossimo modello
          }
        }
      } catch (error) {
        console.error(`❌ [Phase 1] Errore fetch con ${analysisModel.label} (${apiKeyObj.label}):`, error);
        if (keyAttempt < API_KEYS.length - 1) {
          rotateToNextKey();
          continue;
        }
      }
    }
    
    // Se abbiamo successo, esci dal loop dei modelli
    if (phase1Data) {
      break;
    }
    
    // Se non è l'ultimo modello, proviamo il successivo
  }
  
  if (!phase1Data) {
    console.error('❌ [Phase 1] Tutti i modelli e tutte le chiavi hanno fallito');
    return null; // Fallback a LastFM
  }
  const phase1Text = phase1Data.candidates?.[0]?.content?.parts?.[0]?.text;
  
  if (!phase1Text) {
    console.error('❌ [Phase 1] Nessun contenuto nella risposta');
    return null;
  }
  
  // Parse analisi
  let analysisClean = phase1Text.trim();
  analysisClean = analysisClean.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  
  // Estrai JSON se necessario
  const firstBrace = analysisClean.indexOf('{');
  const lastBrace = analysisClean.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace !== -1) {
    analysisClean = analysisClean.substring(firstBrace, lastBrace + 1);
  }
  
  let analysis;
  try {
    analysis = JSON.parse(analysisClean);
  } catch (e) {
    console.error('❌ [Phase 1] Errore parsing analisi:', e.message);
    console.error('Testo ricevuto:', analysisClean.substring(0, 500));
    return null;
  }
  
  
  geminiLogger.logApiSuccess({
    context: 'two_phase_analysis',
    phase: 1,
    artistName,
    model: usedModelLabel,
    responseTime: phase1Time,
    usageMetadata: phase1Data.usageMetadata,
    analysis: analysis
  });
  
  // ========== FASE 2: GENERAZIONE LISTA (Gemini 2.0 Flash) ==========
  const phase2Prompt = getPromptV2Phase2(artistName, analysis, GENERATE_ARTIST_INFO);
  
  geminiLogger.logApiCall({
    context: 'two_phase_generation',
    phase: 2,
    artistName,
    model: 'Gemini 2.0 Flash',
    promptLength: phase2Prompt.length,
    analysisUsed: analysis
  });
  
  const phase2StartTime = Date.now();
  
  // Usa più token quando generiamo anche il testo descrittivo bilingue
  const phase2MaxTokens = GENERATE_ARTIST_INFO ? 16384 : 8192;
  
  // Usa configurazione unificata - primo modello disponibile per similar artists
  const similarModels = getModelsForContext('similarArtists');
  const generationModel = similarModels.length > 0 ? similarModels[0] : GEMINI_MODELS_CONFIG[0];
  const apiVersion2 = generationModel.version || 'v1beta';
  
  let phase2Response = null;
  let phase2Data = null;
  let phase2Time = 0;
  const maxRetries2 = API_KEYS.length; // Prova tutte le chiavi disponibili
  
  for (let attempt = 0; attempt < maxRetries2; attempt++) {
    const apiKeyObj = getCurrentApiKey();
    
    try {
      const attemptStart = Date.now();
      
      const response = await fetch(
        `https://generativelanguage.googleapis.com/${apiVersion2}/models/${generationModel.name}:generateContent?key=${apiKeyObj.key}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: phase2Prompt }] }],
            generationConfig: {
              temperature: 0.8,
              topK: 50,
              topP: 0.96,
              maxOutputTokens: phase2MaxTokens
            }
          })
        }
      );
      
      phase2Time = Date.now() - attemptStart;
      
      if (response.ok) {
        phase2Response = response;
        phase2Data = await response.json();
        break; // Successo!
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error?.message || 'Unknown error';
        
        geminiLogger.logApiError({
          context: 'two_phase_generation',
          phase: 2,
          artistName,
          model: `Gemini 2.0 Flash (${apiKeyObj.label})`,
          status: response.status,
          errorMessage: errorMsg,
          responseTime: phase2Time
        });
        
        // Se è rate limit (429) o overloaded (503), prova la prossima chiave
        if ((response.status === 429 || response.status === 503) && attempt < maxRetries2 - 1) {
          rotateToNextKey();
        } else if (attempt >= maxRetries2 - 1) {
          return null;
        }
      }
    } catch (error) {
      if (attempt < maxRetries2 - 1) {
        rotateToNextKey();
      }
    }
  }
  
  const totalTime = Date.now() - totalStartTime;
  
  if (!phase2Data) {
    return null; // Fallback
  }
  
  const phase2Text = phase2Data.candidates?.[0]?.content?.parts?.[0]?.text;
  
  if (!phase2Text) {
    return null;
  }
  
  // Parse lista artisti
  let artistsClean = phase2Text.trim();
  artistsClean = artistsClean.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  
  // Estrai JSON se necessario
  const firstBrace2 = artistsClean.indexOf('{');
  const lastBrace2 = artistsClean.lastIndexOf('}');
  if (firstBrace2 !== -1 && lastBrace2 !== -1) {
    artistsClean = artistsClean.substring(firstBrace2, lastBrace2 + 1);
  }
  
  let parsedData;
  try {
    parsedData = JSON.parse(artistsClean);
  } catch (e) {
    return null;
  }
  
  if (!parsedData.artists || !Array.isArray(parsedData.artists)) {
    return null;
  }
  
  // Estrai artist_info bilingua se presente
  const artistInfoIt = parsedData.artist_info_it || null;
  const artistInfoEn = parsedData.artist_info_en || null;
  const artistInfo = (artistInfoIt && artistInfoEn) ? { it: artistInfoIt, en: artistInfoEn } : null;
  
  if (artistInfo && GENERATE_ARTIST_INFO) {
    // No verbose logging
  }
  
  // Converti nel formato compatibile con LastFM
  const artists = parsedData.artists.map(artist => ({
    name: artist.name,
    match: artist.match.toString(),
    url: `https://www.last.fm/music/${encodeURIComponent(artist.name)}`,
    image: []
  }));
  
  geminiLogger.logApiSuccess({
    context: 'two_phase_generation',
    phase: 2,
    artistName,
    model: 'Gemini 2.0 Flash',
    responseTime: phase2Time,
    artistCount: artists.length,
    usageMetadata: phase2Data.usageMetadata
  });
  
  geminiLogger.logPerformance({
    context: 'two_phase_complete',
    artistName,
    phase1Time,
    phase2Time,
    totalTime,
    artistCount: artists.length,
    strategy: 'two_phase',
    promptVersion: PROMPT_VERSION,
    phase1Model: usedModelLabel,
    phase2Model: 'Gemini 2.0 Flash',
    totalTokens: (phase1Data.usageMetadata?.totalTokenCount || 0) + (phase2Data.usageMetadata?.totalTokenCount || 0)
  });
  
  // Cache DISABILITATA completamente - non salvare
  // const cacheKey = spotifyId ? `${artistName.toLowerCase()}_${spotifyId}` : artistName.toLowerCase();
  // geminiCache.set(cacheKey, artists);
  
  // Restituisci risultati con artist_info opzionale
  return {
    artists,
    artistInfo: artistInfo // null se non generato
  };
}
*/

/**
 * Ottiene artisti simili usando Gemini AI
 * @param {string} artistName - Nome dell'artista
 * @param {string} spotifyId - ID Spotify (opzionale, per evitare omonimi)
 * @param {Array<string>} genres - Generi Spotify (opzionale, per disambiguazione)
 * @returns {Promise<Array>} - Array di artisti simili con formato compatibile LastFM
 */
export const getSimilarArtistsFromGemini = async (artistName, spotifyId = null, genres = null) => {
  // Se Gemini è disabilitato o non ci sono chiavi API, ritorna null per usare fallback
  if (!USE_GEMINI || API_KEYS.length === 0) {
    return null;
  }

  // Cache DISABILITATA completamente
  // const cacheKey = spotifyId ? `${artistName.toLowerCase()}_${spotifyId}` : artistName.toLowerCase();
  // 
  // // Controlla cache
  // if (geminiCache.has(cacheKey)) {
  //   console.log(`[Gemini] Cache hit for "${artistName}"${spotifyId ? ` (ID: ${spotifyId})` : ''}`);
  //   geminiLogger.logCache({
  //     hit: true,
  //     artistName,
  //     spotifyId,
  //     context: 'similar_artists'
  //   });
  //   return geminiCache.get(cacheKey);
  // }
  
  geminiLogger.logCache({
    hit: false,
    artistName,
    spotifyId,
    context: 'similar_artists'
  });

  // STRATEGIA A 2 FASI DISABILITATA per artisti simili (usa solo single phase per velocità)
  // if (USE_TWO_PHASE_STRATEGY && PROMPT_VERSION === 2) {
  //   console.log(`%c[Gemini] 🚀 TWO-PHASE STRATEGY per "${artistName}"`, 'background: #9C27B0; color: white; padding: 4px 8px; border-radius: 3px; font-weight: bold;');
  //   const result = await getTwoPhaseRecommendations(artistName, spotifyId, genres);
  //   
  //   // Se result è null (errore), ritorna null per fallback
  //   if (!result) return null;
  //   
  //   // Restituisci oggetto con artists e artistInfo
  //   return result; // { artists: [...], artistInfo: "..." | null }
  // }

  // Timer per misurare tempo totale
  const singlePhaseStartTime = Date.now();

  // Prompt semplificato per similar artists
  const prompt = `Find 50 artists similar to "${artistName}".

Return ONLY valid JSON:
{"artists":[{"name":"Artist Name","genre":"genre","origin":"Country"},...]}

RULES:
- Only REAL artists
- Max 50 artists
- All artists must be verifiable
- **CRITICAL: Preserve EXACT spelling, including special characters, accents, and diacritics (e.g., "La Niña" NOT "La Nina", "José" NOT "Jose", "Björk" NOT "Bjork")**
- Use the official/correct spelling of artist names as they appear in music databases`;

  // Usa configurazione unificata da aiProvider.js
  const modelsForSimilarArtists = getModelsForContext('similarArtists');
  if (modelsForSimilarArtists.length === 0) {
    console.warn('[Similar Artists] Nessun modello disponibile');
    return null;
  }
  
  // Prova tutti i modelli configurati in cascata
  for (let i = 0; i < modelsForSimilarArtists.length; i++) {
    const model = modelsForSimilarArtists[i];
    
    // Per ogni modello, prova tutte le chiavi API disponibili
    for (let keyAttempt = 0; keyAttempt < API_KEYS.length; keyAttempt++) {
      const apiKeyObj = getCurrentApiKey();
      const callStartTime = Date.now();
      
      try {
        const attemptLabel = keyAttempt > 0 ? ` (${apiKeyObj.label})` : '';
        // Using API key logged via geminiLogger

        geminiLogger.logApiCall({
          context: 'similar_artists',
          artistName,
          spotifyId,
          genres,
          model: `${model.label} (${apiKeyObj.label})`,
          modelName: model.name,
          apiVersion: model.version || 'v1beta',
          promptVersion: PROMPT_VERSION,
          attempt: i + 1,
          totalModels: modelsForSimilarArtists.length,
          apiKey: apiKeyObj.label
        });

      const apiVersion = model.version || 'v1beta';
      const apiUrl = `https://generativelanguage.googleapis.com/${apiVersion}/models/${model.name}:generateContent`;
      
        // Gemini 2.5 usa "thinking mode" interno che consuma molti token
        // Serve più spazio per thinking + output
        const maxTokens = model.name.includes('2.5') ? 16384 : 8192;
        
        const response = await fetch(`${apiUrl}?key=${apiKeyObj.key}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: prompt
            }]
          }],
          generationConfig: {
            temperature: 0.8,
            topK: 50,
            topP: 0.96,
              maxOutputTokens: maxTokens,
          }
        })
      });

        const responseTime = Date.now() - callStartTime;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
          const errorMsg = errorData.error?.message || 'Unknown error';
          
          geminiLogger.logApiError({
            context: 'similar_artists',
            artistName,
            model: `${model.label} (${apiKeyObj.label})`,
            status: response.status,
            statusText: response.statusText,
            errorData,
            responseTime,
            errorMessage: errorMsg
          });
          
          // Se è quota esaurita (429) o overloaded (503), prova la prossima chiave
          if (response.status === 429 || response.status === 503) {
            geminiLogger.logRateLimit({
              context: 'similar_artists',
              artistName,
              model: `${model.label} (${apiKeyObj.label})`,
              status: response.status,
              attempt: keyAttempt + 1,
              willRetry: keyAttempt < API_KEYS.length - 1
            });
            
            // Se ci sono altre chiavi da provare, ruota e continua
            if (keyAttempt < API_KEYS.length - 1) {
              rotateToNextKey();
              continue; // Prova prossima chiave per questo modello
            } else {
              // Esaurite tutte le chiavi per questo modello, passa al prossimo modello
              if (i < modelsForSimilarArtists.length - 1) {
                geminiLogger.logModelFallback({
                  context: 'similar_artists',
                  artistName,
                  fromModel: model.label,
                  toModel: modelsForSimilarArtists[i + 1].label,
                  reason: 'all_keys_rate_limited'
                });
              }
              break; // Esci dal loop delle chiavi, passa al prossimo modello
            }
        }
        
        // Altri errori: fallback diretto a LastFM
        return null;
      }

      const data = await response.json();
      
      // Estrai il testo dalla risposta di Gemini
      const textContent = data.candidates?.[0]?.content?.parts?.[0]?.text;
      const finishReason = data.candidates?.[0]?.finishReason;
      
      if (!textContent) {
        // Diagnosi specifica per MAX_TOKENS
        if (finishReason === 'MAX_TOKENS') {
          const thoughtsTokens = data.usageMetadata?.thoughtsTokenCount || 0;
          const totalTokens = data.usageMetadata?.totalTokenCount || 0;
          
          geminiLogger.logApiError({
            context: 'similar_artists',
            artistName,
            model: model.label,
            responseTime,
            errorMessage: 'MAX_TOKENS: thinking consumed all available tokens',
            finishReason,
            thoughtsTokenCount: thoughtsTokens,
            totalTokenCount: totalTokens,
            maxOutputTokens: maxTokens,
            responseStructure: JSON.stringify(data, null, 2).substring(0, 500)
          });
        } else {
          geminiLogger.logApiError({
            context: 'similar_artists',
            artistName,
            model: model.label,
            responseTime,
            errorMessage: `No text content in response (finishReason: ${finishReason || 'unknown'})`,
            finishReason,
            responseStructure: JSON.stringify(data, null, 2).substring(0, 500)
          });
        }
        
        continue; // Prova il prossimo modello
      }

      // Pulisci il testo da eventuali markdown code blocks
      let cleanedText = textContent.trim();
      cleanedText = cleanedText.replace(/```json\n?/g, '');
      cleanedText = cleanedText.replace(/```\n?/g, '');
      cleanedText = cleanedText.trim();

      // Parse JSON
      let parsedData;
      try {
        // Primo tentativo: prova a parsare direttamente
        parsedData = JSON.parse(cleanedText);
        
        geminiLogger.logJsonParsing({
          context: 'similar_artists',
          artistName,
          model: model.label,
          success: true,
          textLength: cleanedText.length
        });
        
      } catch (parseError) {
        geminiLogger.logJsonParsing({
          context: 'similar_artists',
          artistName,
          model: model.label,
          success: false,
          error: parseError.message,
          textLength: cleanedText.length
        });
        
        // Tentativo 2: Cerca il JSON nel testo (Gemini 2.5 a volte aggiunge testo prima/dopo)
        try {
          // Cerca il primo { e l'ultimo }
          const firstBrace = cleanedText.indexOf('{');
          const lastBrace = cleanedText.lastIndexOf('}');
          
          if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
            const jsonString = cleanedText.substring(firstBrace, lastBrace + 1);
            parsedData = JSON.parse(jsonString);
            
            geminiLogger.logJsonParsing({
              context: 'similar_artists',
              artistName,
              model: model.label,
              success: true,
              textLength: cleanedText.length,
              jsonExtracted: true,
              method: 'brace_extraction'
            });
          } else {
            throw new Error('No valid JSON found in text');
          }
        } catch (secondError) {
          // Se anche l'estrazione fallisce, logga l'errore completo
          console.error(`[Gemini] ${model.label} JSON parse fallito anche dopo estrazione`);
          console.error(`[Gemini] Testo ricevuto (primi 500 caratteri):`, cleanedText.substring(0, 500));
          console.error(`[Gemini] Lunghezza totale testo:`, cleanedText.length);
          
          if (cleanedText.length > 500) {
            console.error(`[Gemini] Testo ricevuto (ultimi 200 caratteri):`, cleanedText.substring(cleanedText.length - 200));
          }
          
          geminiLogger.logJsonParsing({
            context: 'similar_artists',
            artistName,
            model: model.label,
            success: false,
            error: `${parseError.message} (extraction also failed: ${secondError.message})`,
            textSample: cleanedText.substring(0, 500),
            textEnd: cleanedText.substring(Math.max(0, cleanedText.length - 200)),
            textLength: cleanedText.length,
            finishReason
          });
          
          continue; // Prova il prossimo modello
        }
      }
      
      if (!parsedData.artists || !Array.isArray(parsedData.artists)) {
        geminiLogger.logApiError({
          context: 'similar_artists',
          artistName,
          model: model.label,
          responseTime,
          errorMessage: 'Invalid response format: missing or invalid artists array',
          parsedData: JSON.stringify(parsedData).substring(0, 200)
        });
        
        continue; // Prova il prossimo modello
      }

      // Converti nel formato compatibile con LastFM
      const artists = parsedData.artists.map(artist => ({
        name: artist.name,
        match: artist.match.toString(), // LastFM usa string
        url: `https://www.last.fm/music/${encodeURIComponent(artist.name)}`,
        image: [] // Verrà popolato da Spotify
      }));

      const totalTime = Date.now() - singlePhaseStartTime;
      
      geminiLogger.logApiSuccess({
        context: 'similar_artists',
        artistName,
        model: model.label,
        responseTime,
        totalTime,
        artistCount: artists.length,
        usageMetadata: data.usageMetadata,
        promptTokens: data.usageMetadata?.promptTokenCount,
        candidatesTokens: data.usageMetadata?.candidatesTokenCount,
        totalTokens: data.usageMetadata?.totalTokenCount,
        promptVersion: PROMPT_VERSION
      });
      
      geminiLogger.logPerformance({
        context: 'similar_artists',
        artistName,
        model: model.label,
        responseTime,
        totalTime,
        artistCount: artists.length,
        attempt: i + 1,
        promptVersion: PROMPT_VERSION
      });
      
      // Cache DISABILITATA completamente - non salvare
      // geminiCache.set(cacheKey, artists);
      
        return artists; // SUCCESSO! Ritorna i risultati da entrambi i loop

    } catch (error) {
        const errorTime = Date.now() - callStartTime;
        
        geminiLogger.logApiError({
          context: 'similar_artists',
          artistName,
          model: `${model.label} (${apiKeyObj.label})`,
          responseTime: errorTime,
          errorMessage: error.message,
          errorStack: error.stack,
          keyAttempt: keyAttempt + 1,
          modelAttempt: i + 1,
          willRetry: keyAttempt < API_KEYS.length - 1 || i < modelsForSimilarArtists.length - 1
        });
        
        console.error(`❌ [Gemini] ${model.label} con ${apiKeyObj.label} fetch error:`, error.message);
        
        // Se ci sono altre chiavi da provare per questo modello, ruota
        if (keyAttempt < API_KEYS.length - 1) {
          rotateToNextKey();
          continue; // Prova prossima chiave per questo modello
        } else {
          // Esaurite tutte le chiavi per questo modello, passa al prossimo modello
          if (i < modelsForSimilarArtists.length - 1) {
            geminiLogger.logModelFallback({
              context: 'similar_artists',
              artistName,
              fromModel: model.label,
              toModel: modelsForSimilarArtists[i + 1].label,
              reason: 'all_keys_failed_exception',
              errorMessage: error.message
            });
          }
          break; // Esci dal loop chiavi, passa al prossimo modello
        }
      }
    } // Fine loop chiavi API per questo modello
  } // Fine loop modelli
  
  // Se tutti i modelli hanno fallito, ritorna null per fallback a LastFM
  return null;
};

/**
 * Resetta la cache di Gemini per un artista specifico
 * @param {string} artistName - Nome dell'artista
 */
export const resetGeminiCache = (artistName) => {
  const cacheKey = artistName.toLowerCase();
  if (geminiCache.has(cacheKey)) {
    geminiCache.delete(cacheKey);
  }
};

/**
 * Verifica se Gemini è abilitato e configurato
 * @returns {boolean}
 */
export const isGeminiEnabled = () => {
  return USE_GEMINI && API_KEYS.length > 0;
};

/**
 * Ottiene statistiche sulla cache di Gemini
 * @returns {object}
 */
export const getGeminiCacheStats = () => {
  return {
    size: geminiCache.size,
    artists: Array.from(geminiCache.keys()),
    enabled: isGeminiEnabled()
  };
};

/**
 * Verifica se il salvataggio su DB è abilitato
 * @returns {boolean}
 */
export const isDbCacheEnabled = () => {
  return ENABLE_DB_CACHE;
};

/**
 * PROMPT PER TRACCE SIMILI - Analisi approfondita con strategia a cerchi concentrici
 */
const getTrackSimilarityPrompt = (trackName, artistName, spotifyTrackId = null) => {
  const identityInfo = spotifyTrackId 
    ? `"${trackName}" by ${artistName} (Spotify Track ID: ${spotifyTrackId})`
    : `"${trackName}" by ${artistName}`;
  
  return `You're an expert music curator. Find 50 tracks similar to ${identityInfo}.

${spotifyTrackId ? `Use Spotify Track ID ${spotifyTrackId} to identify the EXACT track.` : ``}

CRITICAL RULES:
1. DO NOT include tracks by ${artistName} (the same artist). The purpose is to discover NEW music from DIFFERENT artists that sound similar.
2. MAXIMUM 3 TRACKS PER ARTIST: To ensure variety, limit each artist/band to maximum 3 tracks in the entire list of 50. This prevents repetitive recommendations from the same artist (e.g., don't include 20 Verve songs when searching Oasis tracks - max 3 Verve tracks).

STEP 1 - DEEP TRACK ANALYSIS (analyze this internally before recommending):

1. GENERRE E SOTTOGENERI:
   - Identify the main musical genre (Rock, Pop, Electronic, Jazz, Classical, etc.)
   - Identify the SPECIFIC subgenre (Indie Rock, Synth-Pop, Trap, Smooth Jazz, etc.)
   - This is the fundamental starting point.

2. STRUTTURA E ARRANGIAMENTO:
   - Form: How is the song structured (Intro, Verse, Pre-chorus, Chorus, Bridge, Outro)?
   - Instrumentation: Which instruments dominate (electric guitar, synthesizers, strings, acoustic/electronic drums)?
   - Density: Is the arrangement minimalist or rich and layered?

3. ELEMENTI ARMONICI E MELODICI:
   - Key and Mode: Is it in major (generally "happy") or minor (generally "melancholic")?
   - Harmonic Progression: Are harmonies simple and repetitive (classic pop/rock) or complex and changing (jazz/progressive)?
   - Melody: Is the vocal line wide and virtuosic or more linear and rhythmic?

4. RITMO E GROOVE:
   - Tempo (BPM): Is it slow (Ballad), medium, or fast (Dance/Punk)?
   - Time Signature: Is it 4/4 (most common) or unusual (3/4, 5/4, etc.)?
   - Rhythm Type: Is the rhythm straight and pulsating (Rock/Dance) or more syncopated and relaxed (R&B/Funk)?

5. TIMBRO E PRODUZIONE:
   - Sound Quality: Is the sound modern and crystalline or more lo-fi and "vintage"?
   - Reverb/Effects: Is there a lot of reverb, delay, or other effects creating a specific atmosphere?
   - Dynamics: Does the song have constant volume or large differences between verse and chorus?

6. ATMOSFERA, EMOZIONE E LIRICHE:
   - Mood: What's the general atmosphere (melancholic, energetic, dreamy, aggressive)?
   - Themes: What are the main themes in the lyrics (love, protest, introspection)?

7. ARTISTA E PERIODO STORICO/GEOGRAFICO:
   - Context: Who is the artist and when was the track released? This helps identify influences and contemporary/similar artists.

STEP 2 - RECOMMENDATION STRATEGY (50 tracks using concentric circles approach):

CIRCLE 1: IDENTITY (Top 10 most similar tracks)
   - Focus: Tracks that share simultaneously Genre, Atmosphere, Instrumentation, and similar Artist/Period.
   - Criteria: Same subgenre and very similar production sound.
   - Example: Same artist or artists heavily influenced by them.

CIRCLE 2: GENRE AND ATMOSPHERE (Next 20 tracks)
   - Focus: Tracks that share the Subgenre and main Mood, but may have small variations in arrangement or historical period.
   - Criteria: Similar energy, basic instrumentation (e.g., both very piano-driven).
   - Example: Contemporary artists in the same subgenre.

CIRCLE 3: SPECIFIC ELEMENTS (Next 10 tracks)
   - Focus: Tracks that share a distinctive technical or production element, even if genre is slightly different.
   - Criteria: Same distinctive harmonic progression, use of same type of synthesizer/vocal sound, or same Producer/Sound Engineer.
   - Example: Artists using identical electronic drum timbre, even if genres differ (e.g., Pop and Hip-Hop).

CIRCLE 4: INFLUENCE AND CONTROLLED CONTRAST (Last 10 tracks)
   - Focus: Tracks representing the original artist's influences or tracks from different genres sharing a common emotion or rhythm.
   - Criteria: Tracks that clearly inspired the original, or tracks offering contrast but maintaining a link (e.g., same tempo and melancholic mood, but one acoustic and one electronic).

This way, we move from almost identical similarity to more conceptual similarity, providing a list of 50 tracks that satisfy both closeness (Circles 1 and 2) and quantity and stimulating variety (Circles 3 and 4).

IMPORTANT:
- All tracks must be REAL and verifiable
- Order by similarity from highest to lowest
- Match scores: 0.95 (very similar) down to 0.55 (interesting tangential connection)
- Multiple tracks can share the SAME score if equally similar
- Format: Track Name - Artist Name

Return ONLY valid JSON (no markdown, no explanation):
{
  "tracks": [
    {"track": "Track Name", "artist": "Artist Name", "match": 0.89, "genre": "specific subgenre"},
    {"track": "Another Track", "artist": "Another Artist", "match": 0.85, "genre": "specific subgenre"},
    ...
  ]
}`;
};

/**
 * PROMPT FASE 1 TRACCE - Analisi profonda della traccia (per Gemini 2.5 Flash)
 */
const getTrackPhase1Prompt = (trackName, artistName, spotifyTrackId = null) => {
  const identityInfo = spotifyTrackId 
    ? `"${trackName}" by ${artistName} (Spotify Track ID: ${spotifyTrackId})`
    : `"${trackName}" by ${artistName}`;
  
  return `Analyze ${identityInfo} for music recommendations.${spotifyTrackId ? ` Use Spotify Track ID ${spotifyTrackId}.` : ''}

Provide concise profile:
- Origin (city/country), language, cultural scene
- Genre (broad + specific subgenre)
- Musical/production style, sound
- Lyrical themes, tone
- Scene position, influences
- Target audience, unique qualities
- Track structure, arrangement, tempo, mood

Return ONLY valid JSON:
{
  "track_name": "${trackName}",
  "artist_name": "${artistName}",
  "origin": "City, Country",
  "language": "Language/Dialect",
  "scene": "Description of music scene",
  "genre_broad": "Broad genre",
  "genre_specific": "Very specific subgenre",
  "musical_style": "Detailed description",
  "lyrical_themes": ["theme1", "theme2", "theme3"],
  "lyrical_style": "Description",
  "production_style": "Description",
  "sound_signature": "Description",
  "mood": "Overall mood/vibe",
  "energy_level": "high/medium/low",
  "tempo": "slow/medium/fast",
  "structure": "Track structure description",
  "unique_qualities": ["quality1", "quality2"],
  "key_influences": ["influence1", "influence2"],
  "target_audience": "Description of typical fan"
}`;
};

/**
 * PROMPT FASE 2 TRACCE - Generazione lista tracce (per Gemini 2.0 Flash)
 */
const getTrackPhase2Prompt = (trackName, artistName, analysis, generateInfo = false) => {
  const basePrompt = `You're an expert music curator. Find 50 tracks similar to "${trackName}" by ${artistName}.

CRITICAL RULES:
1. DO NOT include tracks by ${artistName} (the same artist). The purpose is to discover NEW music from DIFFERENT artists that sound similar.
2. MAXIMUM 3 TRACKS PER ARTIST: To ensure variety, limit each artist/band to maximum 3 tracks in the entire list of 50. This prevents repetitive recommendations from the same artist (e.g., don't include 20 Verve songs when searching Oasis tracks - max 3 Verve tracks).

TRACK PROFILE (from deep analysis):
${JSON.stringify(analysis, null, 2)}

Based on this detailed profile, find 50 similar tracks from DIFFERENT artists that match on MULTIPLE dimensions:

MATCHING PRIORITY:
1. Same local scene + same specific subgenre + similar themes = 0.85-0.95
2. Same region/language + same subgenre + similar mood = 0.75-0.84
3. Same genre + similar sound/production = 0.65-0.74
4. Genre match with some overlap = 0.55-0.64

IMPORTANT:
- Match on MULTIPLE dimensions (not just genre!)
- Consider: scene, language, lyrical themes, production style, mood, tempo
- Unknown tracks with strong profile match > Famous tracks with weak match
- Order by true similarity (not by fame)
- All tracks must be REAL and verifiable
- EXCLUDE tracks by ${artistName}`;

  if (generateInfo) {
    return basePrompt + `

ADDITIONALLY, create a BILINGUAL DISCURSIVE TEXT about "${trackName}" by ${artistName} (150-200 words per language):

Based on the analysis above, write TWO flowing descriptive paragraphs that capture the essence of this track - one in ITALIAN and one in ENGLISH.

EXCLUDE from the text:
- Track name (we already know what track we're talking about)
- Artist name (we already know who made it)
- Origin/country/city (geographic info)
- Language (linguistic details)  
- Key influences (other artists' names)
- Target audience description

INCLUDE in the text (make it flow naturally):
- Musical genre and specific subgenre
- Musical style and sound characteristics
- Production approach and instrumentation
- Lyrical themes and storytelling style
- Mood, atmosphere, and emotional tone
- Energy level and vibe
- Tempo and rhythm characteristics
- Scene position (underground/mainstream/experimental)
- What makes this track unique

Write in a DISCURSIVE, FLOWING style - like a music journalist describing the track to someone who's never heard of it. Be specific but accessible. Make it engaging.

BOTH texts should convey the SAME information but in natural, idiomatic language for each language.

Return ONLY valid JSON (no markdown, no explanation):
{
  "track_info_it": "Il tuo testo descrittivo fluente in italiano...",
  "track_info_en": "Your flowing descriptive text in English...",
  "tracks": [
    {"track": "Track Name", "artist": "Artist Name", "match": 0.89, "genre": "specific subgenre"},
    {"track": "Another Track", "artist": "Another Artist", "match": 0.85, "genre": "specific subgenre"},
    ...
  ]
}`;
  } else {
    return basePrompt + `

Return ONLY valid JSON (no markdown, no explanation):
{
  "tracks": [
    {"track": "Track Name", "artist": "Artist Name", "match": 0.89, "genre": "specific subgenre"},
    {"track": "Another Track", "artist": "Another Artist", "match": 0.85, "genre": "specific subgenre"},
    ...
  ]
}`;
  }
};

/**
 * STRATEGIA A 2 FASI PER TRACCE: Analisi + Generazione con descrizione
 * Fase 1: Gemini 2.5 Flash analizza la traccia
 * Fase 2: Gemini 2.0 Flash genera la lista usando l'analisi + descrizione bilingue
 */
async function getTwoPhaseTrackRecommendations(trackName, artistName, spotifyTrackId) {
  const totalStartTime = Date.now();
  
  // ========== FASE 1: ANALISI PROFONDA ==========
  const phase1Prompt = getTrackPhase1Prompt(trackName, artistName, spotifyTrackId);
  
  const phase1StartTime = Date.now();
  
  // Modelli da provare in ordine: 2.5 Flash → 2.0 Flash
  const PHASE1_MODELS = [
    { name: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', maxTokens: 16384 },
    { name: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash', maxTokens: 8192 }
  ];
  
  let phase1Data = null;
  let phase1Time = 0;
  let usedModelLabel = null;
  
  // Loop sui modelli (2.5 → 2.0)
  for (let modelIdx = 0; modelIdx < PHASE1_MODELS.length; modelIdx++) {
    const analysisModel = PHASE1_MODELS[modelIdx];
    const apiVersion = 'v1beta';
    
    
    geminiLogger.logApiCall({
      context: 'two_phase_track_analysis',
      phase: 1,
      artistName: `${trackName} by ${artistName}`,
      spotifyId: spotifyTrackId,
      model: analysisModel.label,
      promptLength: phase1Prompt.length
    });
    
    // Loop sulle chiavi API per questo modello
    for (let keyAttempt = 0; keyAttempt < API_KEYS.length; keyAttempt++) {
      const apiKeyObj = getCurrentApiKey();
      
      try {
        const attemptStart = Date.now();
        
        const response = await fetch(
          `https://generativelanguage.googleapis.com/${apiVersion}/models/${analysisModel.name}:generateContent?key=${apiKeyObj.key}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: phase1Prompt }] }],
              generationConfig: {
                temperature: 0.7,
                topK: 40,
                topP: 0.95,
                maxOutputTokens: analysisModel.maxTokens
              }
            })
          }
        );
        
        phase1Time = Date.now() - attemptStart;
        
        if (response.ok) {
          phase1Data = await response.json();
          usedModelLabel = analysisModel.label;
          break; // Successo! Esci dal loop delle chiavi
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMsg = errorData.error?.message || 'Unknown error';
          
          geminiLogger.logApiError({
            context: 'two_phase_track_analysis',
            phase: 1,
            artistName: `${trackName} by ${artistName}`,
            model: `${analysisModel.label} (${apiKeyObj.label})`,
            status: response.status,
            errorMessage: errorMsg,
            responseTime: phase1Time
          });
          
          // Se è rate limit (429) o overloaded (503), prova la prossima chiave
          if ((response.status === 429 || response.status === 503) && keyAttempt < API_KEYS.length - 1) {
            rotateToNextKey();
            continue; // Prova prossima chiave
          } else if (keyAttempt >= API_KEYS.length - 1) {
            break; // Esci dal loop chiavi, passa al prossimo modello
          }
        }
      } catch (error) {
        if (keyAttempt < API_KEYS.length - 1) {
          rotateToNextKey();
          continue;
        }
      }
    }
    
    // Se abbiamo successo, esci dal loop dei modelli
    if (phase1Data) {
      break;
    }
    
    // Se non è l'ultimo modello, proviamo il successivo
  }
  
  if (!phase1Data) {
    return null; // Fallback
  }
  
  const phase1Text = phase1Data.candidates?.[0]?.content?.parts?.[0]?.text;
  
  if (!phase1Text) {
    return null;
  }
  
  // Parse analisi
  let analysisClean = phase1Text.trim();
  analysisClean = analysisClean.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  
  // Estrai JSON se necessario
  const firstBrace = analysisClean.indexOf('{');
  const lastBrace = analysisClean.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace !== -1) {
    analysisClean = analysisClean.substring(firstBrace, lastBrace + 1);
  }
  
  let analysis;
  try {
    analysis = JSON.parse(analysisClean);
  } catch (e) {
    return null;
  }
  
  geminiLogger.logApiSuccess({
    context: 'two_phase_track_analysis',
    phase: 1,
    artistName: `${trackName} by ${artistName}`,
    model: usedModelLabel,
    responseTime: phase1Time,
    usageMetadata: phase1Data.usageMetadata,
    analysis: analysis
  });
  
  // ========== FASE 2: GENERAZIONE LISTA + DESCRIZIONE (Gemini 2.0 Flash) ==========
  const phase2Prompt = getTrackPhase2Prompt(trackName, artistName, analysis, GENERATE_ARTIST_INFO);
  
  geminiLogger.logApiCall({
    context: 'two_phase_track_generation',
    phase: 2,
    artistName: `${trackName} by ${artistName}`,
    model: 'Gemini 2.0 Flash',
    promptLength: phase2Prompt.length,
    analysisUsed: analysis
  });
  
  const phase2StartTime = Date.now();
  
  // Usa più token quando generiamo anche il testo descrittivo bilingue
  const phase2MaxTokens = GENERATE_ARTIST_INFO ? 16384 : 8192;
  
  // Usa configurazione unificata - primo modello disponibile per similar tracks
  const similarModels = getModelsForContext('similarArtists');
  const generationModel = similarModels.length > 0 ? similarModels[0] : GEMINI_MODELS_CONFIG[0];
  const apiVersion2 = generationModel.version || 'v1beta';
  
  let phase2Data = null;
  let phase2Time = 0;
  const maxRetries2 = API_KEYS.length;
  
  for (let attempt = 0; attempt < maxRetries2; attempt++) {
    const apiKeyObj = getCurrentApiKey();
    
    try {
      const attemptStart = Date.now();
      
      const response = await fetch(
        `https://generativelanguage.googleapis.com/${apiVersion2}/models/${generationModel.name}:generateContent?key=${apiKeyObj.key}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: phase2Prompt }] }],
            generationConfig: {
              temperature: 0.8,
              topK: 50,
              topP: 0.96,
              maxOutputTokens: phase2MaxTokens
            }
          })
        }
      );
      
      phase2Time = Date.now() - attemptStart;
      
      if (response.ok) {
        phase2Data = await response.json();
        break; // Successo!
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error?.message || 'Unknown error';
        
        geminiLogger.logApiError({
          context: 'two_phase_track_generation',
          phase: 2,
          artistName: `${trackName} by ${artistName}`,
          model: `Gemini 2.0 Flash (${apiKeyObj.label})`,
          status: response.status,
          errorMessage: errorMsg,
          responseTime: phase2Time
        });
        
        // Se è rate limit (429) o overloaded (503), prova la prossima chiave
        if ((response.status === 429 || response.status === 503) && attempt < maxRetries2 - 1) {
          rotateToNextKey();
        } else if (attempt >= maxRetries2 - 1) {
          console.error('❌ [Phase 2 Tracks] Tutte le chiavi API hanno fallito');
          return null;
        }
      }
    } catch (error) {
      console.error(`❌ [Phase 2 Tracks] Errore fetch con ${apiKeyObj.label}:`, error);
      if (attempt < maxRetries2 - 1) {
        rotateToNextKey();
      }
    }
  }
  
  const totalTime = Date.now() - totalStartTime;
  
  if (!phase2Data) {
    return null; // Fallback
  }
  
  const phase2Text = phase2Data.candidates?.[0]?.content?.parts?.[0]?.text;
  
  if (!phase2Text) {
    console.error('❌ [Phase 2 Tracks] Nessun contenuto nella risposta');
    return null;
  }
  
  // Parse lista tracce
  let tracksClean = phase2Text.trim();
  tracksClean = tracksClean.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  
  // Estrai JSON se necessario
  const firstBrace2 = tracksClean.indexOf('{');
  const lastBrace2 = tracksClean.lastIndexOf('}');
  if (firstBrace2 !== -1 && lastBrace2 !== -1) {
    tracksClean = tracksClean.substring(firstBrace2, lastBrace2 + 1);
  }
  
  let parsedData;
  try {
    parsedData = JSON.parse(tracksClean);
  } catch (e) {
    return null;
  }
  
  if (!parsedData.tracks || !Array.isArray(parsedData.tracks)) {
    return null;
  }
  
  // Estrai track_info bilingua se presente
  const trackInfoIt = parsedData.track_info_it || null;
  const trackInfoEn = parsedData.track_info_en || null;
  const trackInfo = (trackInfoIt && trackInfoEn) ? { it: trackInfoIt, en: trackInfoEn } : null;
  
  if (trackInfo && GENERATE_ARTIST_INFO) {
    // No verbose logging
  }
  
  // Converti nel formato standard
  const tracks = parsedData.tracks.map(track => ({
    track: track.track || track.name,
    artist: track.artist,
    match: track.match.toString(),
    genre: track.genre || '',
    url: `https://www.last.fm/music/${encodeURIComponent(track.artist)}`
  }));
  
  geminiLogger.logApiSuccess({
    context: 'two_phase_track_generation',
    phase: 2,
    artistName: `${trackName} by ${artistName}`,
    model: 'Gemini 2.0 Flash',
    responseTime: phase2Time,
    trackCount: tracks.length,
    usageMetadata: phase2Data.usageMetadata
  });
  
  geminiLogger.logPerformance({
    context: 'two_phase_tracks_complete',
    artistName: `${trackName} by ${artistName}`,
    phase1Time,
    phase2Time,
    totalTime,
    trackCount: tracks.length,
    strategy: 'two_phase_tracks',
    phase1Model: usedModelLabel,
    phase2Model: 'Gemini 2.0 Flash',
    totalTokens: (phase1Data.usageMetadata?.totalTokenCount || 0) + (phase2Data.usageMetadata?.totalTokenCount || 0)
  });
  
  // Cache DISABILITATA completamente - non salvare
  // const cacheKey = spotifyTrackId 
  //   ? `${trackName.toLowerCase()}_${artistName.toLowerCase()}_${spotifyTrackId}` 
  //   : `${trackName.toLowerCase()}_${artistName.toLowerCase()}`;
  // geminiCache.set(cacheKey, { tracks, trackInfo });
  
  // Restituisci risultati con trackInfo opzionale
  return {
    tracks,
    trackInfo: trackInfo // null se non generato
  };
}

/**
 * Ottiene tracce simili usando Gemini AI (con stesso sistema fallback degli artisti)
 * @param {string} trackName - Nome della traccia
 * @param {string} artistName - Nome dell'artista
 * @param {string} spotifyTrackId - ID Spotify (opzionale, per evitare omonimi)
 * @returns {Promise<Array>} - Array di tracce simili
 */
export const getSimilarTracksFromGemini = async (trackName, artistName, spotifyTrackId = null) => {
  // Se Gemini è disabilitato o non ci sono chiavi API, ritorna null
  if (!USE_GEMINI || API_KEYS.length === 0) {
    return null;
  }

  // Cache DISABILITATA completamente
  // const cacheKey = spotifyTrackId 
  //   ? `${trackName.toLowerCase()}_${artistName.toLowerCase()}_${spotifyTrackId}` 
  //   : `${trackName.toLowerCase()}_${artistName.toLowerCase()}`;
  // 
  // // Controlla cache
  // if (geminiCache.has(cacheKey)) {
  //   console.log(`[Gemini Tracks] Cache hit for "${trackName}" by ${artistName}${spotifyTrackId ? ` (ID: ${spotifyTrackId})` : ''}`);
  //   geminiLogger.logCache({
  //     hit: true,
  //     artistName: `${trackName} by ${artistName}`,
  //     spotifyId: spotifyTrackId,
  //     context: 'similar_tracks'
  //   });
  //   const cached = geminiCache.get(cacheKey);
  //   // Se è un oggetto con tracks e trackInfo, ritorna entrambi
  //   if (cached && typeof cached === 'object' && cached.tracks) {
  //     return cached;
  //   }
  //   // Altrimenti ritorna solo le tracce (formato vecchio)
  //   return Array.isArray(cached) ? cached : cached.tracks;
  // }
  
  geminiLogger.logCache({
    hit: false,
    artistName: `${trackName} by ${artistName}`,
    spotifyId: spotifyTrackId,
    context: 'similar_tracks'
  });

  // STRATEGIA A 2 FASI PER TRACCE (se abilitata)
  if (USE_TWO_PHASE_STRATEGY && PROMPT_VERSION === 2) {
    const result = await getTwoPhaseTrackRecommendations(trackName, artistName, spotifyTrackId);
    
    // Se result è null (errore), ritorna null per fallback
    if (!result) return null;
    
    // Restituisci oggetto con tracks e trackInfo
    return result; // { tracks: [...], trackInfo: "..." | null }
  }

  // Fallback a prompt singolo (senza descrizione)
  const prompt = getTrackSimilarityPrompt(trackName, artistName, spotifyTrackId);
  const startTime = Date.now();

  // Prova tutti i modelli in cascata (stesso sistema degli artisti)
  for (let i = 0; i < GEMINI_MODELS.length; i++) {
    const model = GEMINI_MODELS[i];
    
    // Per ogni modello, prova tutte le chiavi API disponibili
    for (let keyAttempt = 0; keyAttempt < API_KEYS.length; keyAttempt++) {
      const apiKeyObj = getCurrentApiKey();
      const callStartTime = Date.now();
      
      try {

        geminiLogger.logApiCall({
          context: 'similar_tracks',
          artistName: `${trackName} by ${artistName}`,
          spotifyId: spotifyTrackId,
          model: `${model.label} (${apiKeyObj.label})`,
          modelName: model.name,
          apiVersion: model.version || 'v1beta',
          attempt: i + 1,
          totalModels: GEMINI_MODELS.length,
          apiKey: apiKeyObj.label
        });

        const apiVersion = model.version || 'v1beta';
        const apiUrl = `https://generativelanguage.googleapis.com/${apiVersion}/models/${model.name}:generateContent`;
        
        // Gemini 2.5 usa "thinking mode" interno che consuma molti token
        const maxTokens = model.name.includes('2.5') ? 16384 : 8192;
        
        const response = await fetch(`${apiUrl}?key=${apiKeyObj.key}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: prompt
              }]
            }],
            generationConfig: {
              temperature: 0.8,
              topK: 50,
              topP: 0.96,
              maxOutputTokens: maxTokens,
            }
          })
        });

        const responseTime = Date.now() - callStartTime;

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMsg = errorData.error?.message || 'Unknown error';
          
          geminiLogger.logApiError({
            context: 'similar_tracks',
            artistName: `${trackName} by ${artistName}`,
            model: `${model.label} (${apiKeyObj.label})`,
            status: response.status,
            statusText: response.statusText,
            errorData,
            responseTime,
            errorMessage: errorMsg
          });
          
          // Se è quota esaurita (429) o overloaded (503), prova la prossima chiave
          if (response.status === 429 || response.status === 503) {
            geminiLogger.logRateLimit({
              context: 'similar_tracks',
              artistName: `${trackName} by ${artistName}`,
              model: `${model.label} (${apiKeyObj.label})`,
              status: response.status,
              attempt: keyAttempt + 1,
              willRetry: keyAttempt < API_KEYS.length - 1
            });
            
            // Se ci sono altre chiavi da provare, ruota e continua
            if (keyAttempt < API_KEYS.length - 1) {
              rotateToNextKey();
              continue; // Prova prossima chiave per questo modello
            } else {
              // Esaurite tutte le chiavi per questo modello, passa al prossimo modello
              if (i < GEMINI_MODELS.length - 1) {
                geminiLogger.logModelFallback({
                  context: 'similar_tracks',
                  artistName: `${trackName} by ${artistName}`,
                  fromModel: model.label,
                  toModel: GEMINI_MODELS[i + 1].label,
                  reason: 'all_keys_rate_limited'
                });
              }
              break; // Esci dal loop delle chiavi, passa al prossimo modello
            }
          }
          
          // Altri errori: fallback diretto
          return null;
        }

        const data = await response.json();
        
        // Estrai il testo dalla risposta di Gemini
        const textContent = data.candidates?.[0]?.content?.parts?.[0]?.text;
        const finishReason = data.candidates?.[0]?.finishReason;
        
        if (!textContent) {
          if (finishReason === 'MAX_TOKENS') {
            const thoughtsTokens = data.usageMetadata?.thoughtsTokenCount || 0;
            const totalTokens = data.usageMetadata?.totalTokenCount || 0;
            
            geminiLogger.logApiError({
              context: 'similar_tracks',
              artistName: `${trackName} by ${artistName}`,
              model: model.label,
              responseTime,
              errorMessage: 'MAX_TOKENS: thinking consumed all available tokens',
              finishReason,
              thoughtsTokenCount: thoughtsTokens,
              totalTokenCount: totalTokens,
              maxOutputTokens: maxTokens
            });
          }
          
          continue; // Prova il prossimo modello
        }

        // Pulisci il testo da eventuali markdown code blocks
        let cleanedText = textContent.trim();
        cleanedText = cleanedText.replace(/```json\n?/g, '');
        cleanedText = cleanedText.replace(/```\n?/g, '');
        cleanedText = cleanedText.trim();

        // Parse JSON
        let parsedData;
        try {
          parsedData = JSON.parse(cleanedText);
          
          geminiLogger.logJsonParsing({
            context: 'similar_tracks',
            artistName: `${trackName} by ${artistName}`,
            model: model.label,
            success: true,
            textLength: cleanedText.length
          });
          
        } catch (parseError) {
          geminiLogger.logJsonParsing({
            context: 'similar_tracks',
            artistName: `${trackName} by ${artistName}`,
            model: model.label,
            success: false,
            error: parseError.message
          });
          
          // Tentativo 2: Cerca il JSON nel testo
          try {
            const firstBrace = cleanedText.indexOf('{');
            const lastBrace = cleanedText.lastIndexOf('}');
            
            if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
              const jsonString = cleanedText.substring(firstBrace, lastBrace + 1);
              parsedData = JSON.parse(jsonString);
              
              geminiLogger.logJsonParsing({
                context: 'similar_tracks',
                artistName: `${trackName} by ${artistName}`,
                model: model.label,
                success: true,
                textLength: cleanedText.length,
                jsonExtracted: true,
                method: 'brace_extraction'
              });
            } else {
              throw new Error('No valid JSON found in text');
            }
          } catch (secondError) {
            geminiLogger.logJsonParsing({
              context: 'similar_tracks',
              artistName: `${trackName} by ${artistName}`,
              model: model.label,
              success: false,
              error: `${parseError.message} (extraction also failed: ${secondError.message})`,
              textSample: cleanedText.substring(0, 500),
              textLength: cleanedText.length,
              finishReason
            });
            
            continue; // Prova il prossimo modello
          }
        }
        
        if (!parsedData.tracks || !Array.isArray(parsedData.tracks)) {
          geminiLogger.logApiError({
            context: 'similar_tracks',
            artistName: `${trackName} by ${artistName}`,
            model: model.label,
            responseTime,
            errorMessage: 'Invalid response format: missing or invalid tracks array',
            parsedData: JSON.stringify(parsedData).substring(0, 200)
          });
          
          continue; // Prova il prossimo modello
        }

        // Converti nel formato standard
        const tracks = parsedData.tracks.map(track => ({
          track: track.track || track.name,
          artist: track.artist,
          match: track.match.toString(),
          genre: track.genre || '',
          // URL LastFM per l'artista (non per la traccia)
          url: `https://www.last.fm/music/${encodeURIComponent(track.artist)}`
        }));

        const totalTime = Date.now() - startTime;
        
        geminiLogger.logApiSuccess({
          context: 'similar_tracks',
          artistName: `${trackName} by ${artistName}`,
          model: model.label,
          responseTime,
          totalTime,
          artistCount: tracks.length,
          usageMetadata: data.usageMetadata,
          promptTokens: data.usageMetadata?.promptTokenCount,
          candidatesTokens: data.usageMetadata?.candidatesTokenCount,
          totalTokens: data.usageMetadata?.totalTokenCount
        });
        
        geminiLogger.logPerformance({
          context: 'similar_tracks',
          artistName: `${trackName} by ${artistName}`,
          model: model.label,
          responseTime,
          totalTime,
          artistCount: tracks.length,
          attempt: i + 1
        });
        
        // Cache DISABILITATA completamente - non salvare
        // geminiCache.set(cacheKey, tracks);
        
        return tracks; // SUCCESSO! Ritorna i risultati

      } catch (error) {
        const errorTime = Date.now() - callStartTime;
        
        geminiLogger.logApiError({
          context: 'similar_tracks',
          artistName: `${trackName} by ${artistName}`,
          model: `${model.label} (${apiKeyObj.label})`,
          responseTime: errorTime,
          errorMessage: error.message,
          errorStack: error.stack,
          keyAttempt: keyAttempt + 1,
          modelAttempt: i + 1,
          willRetry: keyAttempt < API_KEYS.length - 1 || i < GEMINI_MODELS.length - 1
        });
        
        console.error(`[Gemini Tracks] fetch error`, error.message);
        
        // Se ci sono altre chiavi da provare per questo modello, ruota
        if (keyAttempt < API_KEYS.length - 1) {
          rotateToNextKey();
          continue; // Prova prossima chiave per questo modello
        } else {
          // Esaurite tutte le chiavi per questo modello, passa al prossimo modello
          if (i < GEMINI_MODELS.length - 1) {
            geminiLogger.logModelFallback({
              context: 'similar_tracks',
              artistName: `${trackName} by ${artistName}`,
              fromModel: model.label,
              toModel: GEMINI_MODELS[i + 1].label,
              reason: 'all_keys_failed_exception',
              errorMessage: error.message
            });
          }
          break; // Esci dal loop chiavi, passa al prossimo modello
        }
      }
    } // Fine loop chiavi API per questo modello
  } // Fine loop modelli
  
  // Se tutti i modelli hanno fallito, ritorna null
  return null;
};

/**
 * Estrae il testo dalla risposta Gemini (candidate).
 * Con modelli 2.5 Flash/Lite che usano thinking, parts può avere [thinking, risposta]:
 * usiamo l'ultima part con testo come messaggio effettivo; altrimenti parts[0].
 * Così il bridge funziona sia con 2.5 Flash Lite che con 2.5 Flash.
 */
const getTextFromGeminiCandidate = (data) => {
  const parts = data?.candidates?.[0]?.content?.parts;
  if (!parts || !parts.length) return '';
  const withText = parts.map(p => p?.text).filter(Boolean);
  if (withText.length === 0) return '';
  if (withText.length === 1) return withText[0];
  return withText[withText.length - 1];
};

/**
 * Helper: Processa risposta bridge - controlla se è JSON di validazione o testo normale
 * @returns {string|{valid: false, message: string}} Testo normale o oggetto errore
 */
const processBridgeResponse = (text) => {
  if (!text || !text.trim()) return '';
  
  let trimmed = text.trim();
  
  // Cerca JSON all'inizio del testo (anche se seguito da altro testo)
  if (trimmed.startsWith('{')) {
    try {
      // Trova la fine del JSON (prima } che chiude l'oggetto)
      let jsonEnd = -1;
      let braceCount = 0;
      for (let i = 0; i < trimmed.length; i++) {
        if (trimmed[i] === '{') braceCount++;
        if (trimmed[i] === '}') {
          braceCount--;
          if (braceCount === 0) {
            jsonEnd = i;
            break;
          }
        }
      }
      
      if (jsonEnd > 0) {
        const jsonStr = trimmed.substring(0, jsonEnd + 1);
        const json = JSON.parse(jsonStr);
        
        // Se è un oggetto di validazione con valid: false, restituiscilo
        if (json.valid === false && json.message) {
          return json;
        }
        
        // Se è valid: true, rimuovi COMPLETAMENTE il JSON e prendi solo il testo dopo
        if (json.valid === true) {
          // Rimuovi il JSON e tutto lo spazio/newline dopo
          trimmed = trimmed.substring(jsonEnd + 1).trim();
          // Rimuovi anche eventuali newline o spazi multipli all'inizio
          trimmed = trimmed.replace(/^[\n\r\s]+/, '');
          // Se non c'è testo dopo, restituisci stringa vuota
          if (!trimmed) return '';
        }
      }
    } catch (e) {
      // Non è JSON valido, continua come testo normale
    }
  }
  
  // Rimuovi anche JSON che potrebbe essere nel mezzo o alla fine del testo
  // Cerca pattern come {"valid": true} o {"valid":false} ovunque nel testo
  // Rimuovi anche varianti con spazi diversi
  trimmed = trimmed.replace(/\{\s*"valid"\s*:\s*(true|false)\s*\}/g, '').trim();
  trimmed = trimmed.replace(/\{\s*"valid"\s*:\s*"(true|false)"\s*\}/g, '').trim();
  // Rimuovi anche eventuali code blocks JSON
  trimmed = trimmed.replace(/```json\s*\{\s*"valid"\s*:\s*(true|false)\s*\}\s*```/gi, '').trim();
  trimmed = trimmed.replace(/```\s*\{\s*"valid"\s*:\s*(true|false)\s*\}\s*```/gi, '').trim();
  
  // Testo normale - pulisci e restituisci
  let cleanedText = trimmed.replace(/\r\n/g, '\n');
  cleanedText = cleanedText.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n');
  return cleanedText;
};

/**
 * Messaggio bridge rapido (Gemini 2.0 Flash) per il loading screen
 * Intrattiene l'utente mentre la chiamata principale è in corso
 * Restituisce stringa (messaggio) o oggetto {valid: false, message: "..."} se invalido
 */
export const getFreeTextBridgeMessage = async (userRequest, language = 'en') => {
  if (!USE_GEMINI || !userRequest?.trim()) {
    return '';
  }

  // Usa configurazione unificata da aiProvider.js
  const models = getModelsForContext('bridge');
  if (models.length === 0) {
    console.warn('[Free Text Bridge] Nessun modello disponibile');
    return '';
  }
  const model = models[0]; // Bridge usa solo il primo modello
  const bridgeConfig = getBridgeConfig();
  const provider = bridgeConfig.provider; // Recupera provider dalla configurazione

  const promptData = getFreeTextBridgePrompt(userRequest, language);
  const promptLength = (promptData.systemInstruction?.length || 0) + (promptData.userMessage?.length || 0);
  // Strategia: Key 4 e 5 dedicate al bridge, fallback su altre partendo da quelle NON usate dalla 2.5
  const failedKeys = new Set(); // Traccia chiavi che hanno fallito in questo tentativo
  
  // Se il proxy è attivo prova immediatamente il proxy (senza richiedere chiavi locali)
  if (USE_PROXY) {
    const { data, viaProxy } = await attemptProxyCall({
      model: model.name,
      version: model.version || 'v1beta',
      systemInstruction: promptData.systemInstruction,
      prompt: promptData.userMessage, // Solo la parte dinamica
      generationConfig: {
        temperature: 0.55,
        topK: 32,
        topP: 0.9,
        maxOutputTokens: 4096 // Aumentato a 4096 per evitare MAX_TOKENS (2.5 Flash usa molti token per thinking)
      },
      context: 'free_text_bridge',
      keyLabel: 'bridge_proxy',
      provider
    });

    if (data) {
      const finishReason = data.candidates?.[0]?.finishReason;
      const text = getTextFromGeminiCandidate(data);
      
      // Log diagnostico per capire se la risposta è troncata
      if (finishReason === 'MAX_TOKENS') {
        console.warn('[Gemini Bridge] ⚠️ Risposta troncata: MAX_TOKENS raggiunto. Testo ricevuto:', text.substring(0, 200));
      }
      
      if (text.trim()) {
        const processed = processBridgeResponse(text);
        
        if (typeof processed === 'object' && processed.valid === false) {
          console.log('[Gemini Bridge] ❌ Prompt non valido rilevato dal bridge (proxy)');
          return processed;
        }

        geminiLogger.logApiSuccess({
          context: 'free_text_bridge',
          model: `${model.label} (Proxy)`,
          responseTime: data._responseTime || null,
          promptLength,
          outputLength: typeof processed === 'string' ? processed.length : 0,
          finishReason,
          language
        });

        return processed;
      } else {
        console.warn('[Gemini Bridge] ⚠️ Risposta vuota. finishReason:', finishReason);
      }
    } else if (viaProxy) {
      console.warn('[Free Text Bridge] Proxy call failed, attempting local fallback');
    }
  }

  // BRIDGE: Usa SOLO chiavi bridge (mai chiavi principali)
  const bridgeKeys = BRIDGE_API_KEYS.length > 0 ? [...BRIDGE_API_KEYS] : [];
  
  if (bridgeKeys.length === 0) {
    console.warn('[Free Text Bridge] ❌ Nessuna chiave bridge disponibile - il bridge richiede chiavi dedicate');
    return '';
  }
  
  // Ordina: prima quella con last_used più recente (ha funzionato), poi per usage_count
  bridgeKeys.sort((a, b) => {
    if (a.lastUsed && b.lastUsed) {
      return new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime();
    }
    if (a.lastUsed) return -1;
    if (b.lastUsed) return 1;
    return (a.usageCount || 0) - (b.usageCount || 0);
  });
  
  const allKeysToTry = bridgeKeys; // SOLO chiavi bridge

  // Prova fino a tutte le chiavi disponibili
  for (let keyAttempt = 0; keyAttempt < allKeysToTry.length; keyAttempt++) {
    // Seleziona la prima chiave disponibile non fallita
    const apiKeyObj = allKeysToTry.find(key => !failedKeys.has(key.key));
    
    if (!apiKeyObj) {
      console.warn('[Free Text Bridge] Tutte le chiavi hanno fallito');
      return '';
    }
    
    // Aggiorna statistiche uso chiave
    apiKeyObj.usageCount++;
    apiKeyObj.lastUsed = new Date().toISOString();

    geminiLogger.logApiCall({
      context: 'free_text_bridge',
      action: 'start',
      attempt: keyAttempt + 1,
      model: `${model.label} (${apiKeyObj.label})`,
      keyLabel: apiKeyObj.label,
      promptLength,
      language,
      userRequest: userRequest.substring(0, 120)
    });
    
    const attemptStart = Date.now();

    try {
      // Prova prima il proxy se abilitato
      if (USE_PROXY) {
        console.log('[Gemini Bridge] 🔄 Proxy abilitato, tentativo chiamata via proxy...');
        const proxyResult = await callGeminiViaProxy({
          model: model.name,
          version: model.version || 'v1beta',
          prompt,
          generationConfig: {
            temperature: 0.55,
            topK: 32,
            topP: 0.9,
            maxOutputTokens: 4096, // Aumentato a 4096 per evitare MAX_TOKENS (2.5 Flash usa molti token per thinking)
          },
          context: 'free_text_bridge',
          keyLabel: apiKeyObj.label
        });

        if (proxyResult && !proxyResult.error) {
          const responseTime = Date.now() - attemptStart;
          const text = getTextFromGeminiCandidate(proxyResult);
          
          if (text.trim()) {
            const processed = processBridgeResponse(text);
            
            // Se è un oggetto di errore, restituiscilo
            if (typeof processed === 'object' && processed.valid === false) {
              console.log('[Gemini Bridge] ❌ Prompt non valido rilevato dal bridge');
              return processed;
            }
            
            // Testo normale
            geminiLogger.logApiSuccess({
              context: 'free_text_bridge',
              model: `${model.label} (Proxy)`,
              responseTime,
              promptLength,
              outputLength: processed.length,
              language
            });

            return processed;
          }
        }
        // Se proxy fallisce e USE_PROXY è true, NON fare fallback (sicurezza)
        if (USE_PROXY) {
          console.warn('[Gemini Bridge] ⚠️ Proxy fallito - saltando fallback diretto per sicurezza');
          continue; // Prova con la prossima chiave o esci
        }
      }

      // Fallback: chiamata diretta SOLO se proxy disabilitato
      if (!USE_PROXY) {
        console.log('[Gemini Bridge] 🔑 Proxy disabilitato, uso chiamata diretta con chiavi da .env');
      } else {
        // Se USE_PROXY è true, non dovremmo arrivare qui, ma per sicurezza:
        console.warn('[Gemini Bridge] ⚠️ Proxy abilitato - saltando chiamata diretta per sicurezza');
        continue;
      }
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(
        `https://generativelanguage.googleapis.com/${model.version || 'v1beta'}/models/${model.name}:generateContent?key=${apiKeyObj.key}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: {
              temperature: 0.55,
              topK: 32,
              topP: 0.9,
              maxOutputTokens: 4096 // Aumentato a 4096 per evitare MAX_TOKENS (2.5 Flash usa molti token per thinking)
            }
          })
        }
      );

      clearTimeout(timeoutId);

      const responseTime = Date.now() - attemptStart;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const statusCode = response.status;
        const errorMessage = errorData.error?.message || `HTTP ${statusCode}`;

        // Aggiungi questa chiave al Set delle chiavi fallite
        failedKeys.add(apiKeyObj.key);

        geminiLogger.logApiError({
          context: 'free_text_bridge',
          model: `${model.label} (${apiKeyObj.label})`,
          status: statusCode,
          errorMessage,
          responseTime,
          promptLength,
          language,
          keyAttempt: keyAttempt + 1
        });

        // Se ci sono altre chiavi da provare, continua
        if (keyAttempt < allKeysToTry.length - 1) {
          continue;
        }

        // Se è un errore di autenticazione e non ci sono altre chiavi, esci
        if (statusCode === 401 || statusCode === 403) {
          return '';
        }

        // Altrimenti esci se non ci sono altre chiavi
        return '';
      }

      const data = await response.json();
      const finishReason = data.candidates?.[0]?.finishReason;
      const text = getTextFromGeminiCandidate(data);
      
      // Log diagnostico per capire se la risposta è troncata
      if (finishReason === 'MAX_TOKENS') {
        console.warn('[Gemini Bridge] ⚠️ Risposta troncata: MAX_TOKENS raggiunto. Testo ricevuto:', text.substring(0, 200));
      }
      
      if (!text.trim()) {
        geminiLogger.logApiError({
          context: 'free_text_bridge',
          model: `${model.label} (${apiKeyObj.label})`,
          responseTime,
          errorMessage: `Empty response (finishReason: ${finishReason || 'unknown'})`,
          finishReason,
          promptLength,
          language
        });
        return '';
      }

      const processed = processBridgeResponse(text);
      
      // Se è un oggetto di errore, restituiscilo
      if (typeof processed === 'object' && processed.valid === false) {
        console.log('[Gemini Bridge] ❌ Prompt non valido rilevato dal bridge');
        return processed;
      }
      
      // Testo normale
      geminiLogger.logApiSuccess({
        context: 'free_text_bridge',
        model: `${model.label} (${apiKeyObj.label})`,
        responseTime,
        promptLength,
        outputLength: processed.length,
        language
      });

      return processed;
    } catch (error) {
      const responseTime = Date.now() - attemptStart;

      // Aggiungi questa chiave al Set delle chiavi fallite
      failedKeys.add(apiKeyObj.key);

      geminiLogger.logApiError({
        context: 'free_text_bridge',
        model: `${model.label} (${apiKeyObj.label})`,
        responseTime,
        errorMessage: error.message,
        language,
        promptLength,
        keyAttempt: keyAttempt + 1,
        willRetry: keyAttempt < allKeysToTry.length - 1
      });

      console.warn(`[Free Text Bridge] Errore con ${apiKeyObj.label}:`, error.message);

      // Se ci sono altre chiavi da provare, continua
      if (keyAttempt < allKeysToTry.length - 1) {
        continue;
      }
    }
  }

  console.warn('[Free Text Bridge] Tutte le chiavi hanno fallito');
  return '';
};

/**
 * FOUNDLY 2.0 - Ricerca libera con prompt testuale/vocale
 * Analizza la richiesta dell'utente e genera raccomandazioni o risposta negativa
 */
export const getFreeTextSearchFromGemini = async (userRequest, searchType = 'artist', language = 'en') => {
  if (!USE_GEMINI) {
    console.log('[Free Text Search] AI disabled, returning null');
    return null;
  }

  // Usa configurazione unificata da aiProvider.js
  const modelsToTry = getModelsForContext('freeTextSearch');
  if (modelsToTry.length === 0) {
    console.warn('[Free Text Search] Nessun modello disponibile');
    return null;
  }
  const freeTextConfig = getFreeTextSearchConfig();
  const provider = freeTextConfig.provider; // Recupera provider dalla configurazione

  const forcedSearchType = 'artist';
  const startTime = Date.now();
  const promptData = getFreeTextSearchPrompt(userRequest, forcedSearchType, language);
  const promptLength = (promptData.systemInstruction?.length || 0) + (promptData.userMessage?.length || 0);
  const generationConfigForModel = (model) => ({
    temperature: 0.2, 
    topK: 20, 
    topP: 0.85, 
    maxOutputTokens: model.name.includes('2.5') || model.name.includes('3') ? 16384 : 4096
  });

  for (let i = 0; i < modelsToTry.length; i++) {
    const model = modelsToTry[i];

    const genConfig = generationConfigForModel(model);
    console.log(`[Free Text Search] 🔧 Configurazione generazione:`, {
      temperature: genConfig.temperature,
      topK: genConfig.topK,
      topP: genConfig.topP,
      model: model.name,
      userRequest: userRequest.substring(0, 50) + '...'
    });
    
    const { data } = await attemptProxyCall({
      model: model.name,
      version: model.version || 'v1beta',
      systemInstruction: promptData.systemInstruction,
      prompt: promptData.userMessage, // Solo la parte dinamica
      generationConfig: genConfig,
      context: 'free_text_search',
      keyLabel: `${model.label}-proxy`,
      provider
    });

    if (!data) {
      console.warn(`[Free Text Search] Proxy response empty per ${model.label}`);
      // Se il proxy è abilitato, NON fare fallback a chiamate dirette (sicurezza)
      if (USE_PROXY) {
        console.warn(`[Free Text Search] Proxy abilitato - saltando fallback diretto per sicurezza`);
        continue;
      }
      
      // Fallback diretto solo se proxy disabilitato
      // Nota: fallback diretto usa prompt completo (non ottimizzato per cache)
      const fallbackPrompt = promptData.systemInstruction + '\n\n' + promptData.userMessage;
      const directResult = await callGeminiDirectWithKeys({
        model,
        prompt: fallbackPrompt,
        generationConfig: generationConfigForModel(model),
        context: 'free_text_search',
        userRequest,
        language,
        startTime,
        promptLength
      });

      if (directResult) {
        return directResult;
      }

      continue;
    }

    const result = processFreeTextProxyResponse({
      data,
      model,
      userRequest,
      prompt: promptData.systemInstruction + '\n\n' + promptData.userMessage, // Ricostruisci per logging
      language,
      startTime,
      promptLength
    });

    if (result) {
      return result;
    }
  }

  console.error('[Free Text Search] Nessuna risposta valida dal proxy Gemini');
  
  // Se il proxy è abilitato, NON fare fallback a chiamate dirette (sicurezza)
  if (USE_PROXY) {
    console.error('[Free Text Search] Proxy abilitato - nessun fallback diretto disponibile per sicurezza');
    return null;
  }
  
  // Fallback diretto solo se proxy disabilitato
  // Nota: fallback diretto usa prompt completo (non ottimizzato per cache)
  const fallbackModels = getModelsForContext('freeTextSearch');
  const fallbackModel = fallbackModels.length > 0 ? fallbackModels[0] : GEMINI_MODELS_CONFIG[0];
  const fallbackPrompt = promptData.systemInstruction + '\n\n' + promptData.userMessage;
  const fallbackResult = await callGeminiDirectWithKeys({
    model: fallbackModel,
    prompt: fallbackPrompt,
    generationConfig: generationConfigForModel(fallbackModel),
    context: 'free_text_search',
    userRequest,
    language,
    startTime,
    promptLength
  });

  if (fallbackResult) {
    return fallbackResult;
  }

  return null;
};
